import { useLayoutEffect } from "react";

// Sub-pixel layout still counts as reaching the root's edge.
const EDGE_TOLERANCE = 1;

const setPixels = (element: HTMLElement, name: string, value: number) => {
    const text = `${value}px`;
    if (element.style.getPropertyValue(name) !== text) {
        element.style.setProperty(name, text);
    }
};

const writeScrollbarSizes = (viewport: HTMLElement, root: HTMLElement) => {
    const viewportRect = viewport.getBoundingClientRect();
    const rootRect = root.getBoundingClientRect();
    const isAtRootEnd = rootRect.right - viewportRect.right <= EDGE_TOLERANCE;
    const isAtRootBottom =
        rootRect.bottom - viewportRect.bottom <= EDGE_TOLERANCE;
    // Both are read before either is written, so the second read doesn't
    // force another style pass.
    const yWidth = isAtRootEnd
        ? viewport.offsetWidth - viewport.clientWidth
        : 0;
    const xHeight = isAtRootBottom
        ? viewport.offsetHeight - viewport.clientHeight - viewport.clientTop
        : 0;
    setPixels(root, "--dg-scrollbar-y-w", yWidth);
    setPixels(root, "--dg-scrollbar-x-h", xHeight);
};

/**
 * Publishes on the root (and its direct children) the size of each viewport
 * scrollbar that runs along the root's end or bottom edge, as
 * `--dg-scrollbar-y-w` and `--dg-scrollbar-x-h`: 0 while that axis doesn't
 * overflow, while rows stop short of the bottom, and with overlay scrollbars.
 * A bar floating over the grid's bottom edge uses them to keep clear. Writes
 * styles; never renders.
 */
export const useScrollbarSizeVars = (
    viewport: HTMLElement | null,
    root: HTMLElement | null,
) => {
    useLayoutEffect(() => {
        if (!viewport || !root) {
            return undefined;
        }
        const update = () => writeScrollbarSizes(viewport, root);
        // A scrollbar showing or hiding resizes the viewport's content box,
        // so this also catches overflow that starts or ends.
        const resizeObserver = new ResizeObserver(update);
        resizeObserver.observe(viewport);
        resizeObserver.observe(root);
        update();
        return () => {
            resizeObserver.disconnect();
            root.style.removeProperty("--dg-scrollbar-y-w");
            root.style.removeProperty("--dg-scrollbar-x-h");
        };
    }, [viewport, root]);
};
